import clsx from "clsx";

import { DateTimeTz, Icon } from "@web-app/components";
import { Order, OrderTimeline } from "@web-app/types";
import { timeZoneMap } from "@web-app/utils/helpers";

type CustomFieldActivityPropTypes = {
  item: OrderTimeline;
  order: Order;
  showTimelineBar: boolean;
  timezone: keyof typeof timeZoneMap | undefined;
};

type FieldValue = {
  isSelected?: boolean;
  value: string;
};

function parseFieldValues(jsonString: string): FieldValue[] {
  try {
    return JSON.parse(jsonString);
  } catch {
    return [];
  }
}

function formatFieldValues(
  values: FieldValue[],
  fieldType?: string,
  options?: { label: string; value: string }[],
): string {
  if (!values || values.length === 0) return "(empty)";

  const displayValues = values
    .map((v) => {
      if (typeof v.value === "boolean") {
        return v.value ? "checked" : "unchecked";
      }
      // Handle empty or null values
      if (v.value === null || v.value === undefined || v.value === "") {
        return null;
      }

      // For dropdown type, look up the label from options
      if (fieldType === "DROPDOWN" && options) {
        const option = options.find((opt) => opt.value === String(v.value));
        return option ? option.label : String(v.value);
      }

      return String(v.value);
    })
    .filter((v) => v !== null);

  if (displayValues.length === 0) {
    return "(empty)";
  }

  if (displayValues.length === 1) {
    return displayValues[0];
  }

  return displayValues.join(", ");
}

function getActionConfig(action: string) {
  switch (action) {
    case "CREATE":
      return {
        color: "text-green-600",
        icon: "fasPlus" as const,
        text: "Added",
      };
    case "UPDATE":
      return {
        color: "text-blue-600",
        icon: "fasPenField" as const,
        text: "Updated",
      };
    case "DELETE":
      return {
        color: "text-red-600",
        icon: "fasTrash" as const,
        text: "Removed",
      };
    default:
      return {
        color: "text-gray-600",
        icon: "fasPenField" as const,
        text: "Changed",
      };
  }
}

export default function CustomFieldActivity({
  item,
  order,
  showTimelineBar,
  timezone,
}: CustomFieldActivityPropTypes) {
  const actionConfig = getActionConfig(item.action);

  const fieldId = item.entity.replace("customField_", "");

  const customField = order.customFields.find(
    (cf) => cf.definition.id === fieldId,
  );
  const fieldName = customField?.definition.name || "custom field";
  const fieldType = customField?.definition.type;
  const fieldOptions = customField?.definition.options;

  const previousValues = item.previousValue
    ? parseFieldValues(item.previousValue)
    : [];
  const updatedValues = item.updatedValue
    ? parseFieldValues(item.updatedValue)
    : [];

  const previousDisplay = formatFieldValues(
    previousValues,
    fieldType,
    fieldOptions,
  );
  const updatedDisplay = formatFieldValues(
    updatedValues,
    fieldType,
    fieldOptions,
  );

  return (
    <li key={item.id} className="relative flex gap-x-2 md:gap-x-4">
      <div
        className={clsx(
          showTimelineBar ? "-bottom-6" : "h-6",
          "absolute left-0 top-0 flex w-6 justify-center",
        )}
      >
        <div className="w-px bg-gray-200" />
      </div>
      <div className="relative flex h-7 w-6 flex-none items-center justify-center bg-white">
        <Icon
          name={actionConfig.icon}
          className={clsx("h-3 md:h-4", actionConfig.color)}
        />
      </div>
      <p className="flex-auto flex flex-col items-start py-0.5 text-[10px] md:text-xs leading-0 md:leading-5 text-gray-500">
        <span
          className={clsx(
            "inline-block font-medium text-gray-900",
            actionConfig.color,
          )}
        >
          {actionConfig.text} {fieldName}
          {item.action === "UPDATE" && (
            <span>
              {" from "}
              <span className="font-normal">{previousDisplay}</span>
              {" to "}
              <span className="font-normal">{updatedDisplay}</span>
            </span>
          )}
          {item.action === "CREATE" && (
            <span>
              {" with value "}
              <span className="font-normal">{updatedDisplay}</span>
            </span>
          )}
          {item.action === "DELETE" && (
            <span>
              {" with value "}
              <span className="font-normal">{previousDisplay}</span>
            </span>
          )}
        </span>
        <span className="relative">
          <DateTimeTz
            start={new Date(item?.createdAt)}
            timezone={timezone}
            createdBy={item?.createdBy?.name}
            presentation="timeline"
          />
        </span>
      </p>
    </li>
  );
}
